import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IconProp } from "@fortawesome/fontawesome-svg-core";

interface MenuItem {
  icon: IconProp;
  title: string;
  subtext: string;
  url: string;
}

interface DropdownMenuProps {
  menuItems: MenuItem[];
  onClose: () => void;
}

const DropdownMenu: React.FC<DropdownMenuProps> = ({ menuItems, onClose }) => {
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    if (!isVisible) {
      const timer = setTimeout(onClose, 500); // Wait 500ms before calling onClose
      return () => clearTimeout(timer); // Clear timeout if component unmounts
    }
  }, [isVisible, onClose]);

  const handleMouseLeave = () => {
    setIsVisible(false); // Trigger the reverse animation on mouse leave
  };

  // Styling constants
  const backgroundColor = "bg-[#fcfbf8]";
  const borderColor = "border-[#e3e3de]";
  const textColor = "text-[#04262b]";
  const subtextColor = "text-[#717c79]";
  const iconBgColor = "bg-[#ebebe5]";
  const iconColor = "text-[#04262b]";
  const hoverBgColor = "hover:bg-[#f2f1eb]";

  // Set the width to 375px on non-mobile screens
  const containerWidthClass = "w-full md:w-[375px]";

  const containerClasses = `
    ${backgroundColor}
    ${containerWidthClass}
    rounded-lg
    shadow
    border
    ${borderColor}
    flex flex-col
    transition-transform
    duration-100
    transform
    origin-top
    ${isVisible ? "animate-dropdown" : "animate-dropdown-reverse"}
  `;

  return (
    <div className={containerClasses} onMouseLeave={handleMouseLeave}>
      {menuItems.map((item, index) => (
        <a
          key={index}
          href={item.url}
          className={`flex items-center gap-4 px-5 py-3 rounded transition-all ${hoverBgColor}`}
        >
          <div
            className={`flex-shrink-0 flex items-center justify-center w-12 h-12 rounded ${iconBgColor}`}
          >
            <FontAwesomeIcon
              icon={item.icon}
              className={`text-2xl ${iconColor}`}
              fixedWidth
            />
          </div>
          <div className="flex-1 flex flex-col">
            <div
              className={`${textColor} font-semibold font-['Manrope'] tracking-tight text-base leading-normal`}
            >
              {item.title}
            </div>
            <div
              className={`${subtextColor} text-sm font-medium font-['Manrope'] tracking-tight leading-normal`}
            >
              {item.subtext}
            </div>
          </div>
        </a>
      ))}
    </div>
  );
};

export default DropdownMenu;
